import { useEffect, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { apiGet, apiSend } from '../lib/api'
import { formatWithUnit, registerMetricInfo } from '../lib/format'
import type { CustomMetric, CustomMetrics, Sample } from '../lib/types'

/** Einheiten, wie die Auswahl sie nennt. */
export const EINHEIT_TEXT: Record<string, string> = {
  '': 'ohne Einheit',
  percent: 'Prozent (%)',
  celsius: 'Grad Celsius (°C)',
  volt: 'Volt (V)',
  ampere: 'Ampere (A)',
  watt: 'Watt (W)',
  rpm: 'Umdrehungen (U/min)',
  ms: 'Millisekunden (ms)',
  seconds: 'Sekunden (s)',
  minutes: 'Minuten (min)',
  days: 'Tage',
  bytes: 'Byte',
  bits_per_second: 'Bit je Sekunde',
}

const INTERVALLE: [number, string][] = [
  [30, '30 Sekunden'],
  [60, '1 Minute'],
  [300, '5 Minuten'],
  [900, '15 Minuten'],
  [3600, '1 Stunde'],
]

/** Die Schwellen als Satz: „Warnung unter 3000 U/min · kritisch unter 1500 U/min · nach 2 min“. */
export function schwellenText(c: CustomMetric): string {
  if (c.warn == null && c.crit == null) return 'kein Alarm'
  const wort = c.direction === 'below' ? 'unter' : 'über'
  const teile: string[] = []
  if (c.warn != null) teile.push(`Warnung ${wort} ${formatWithUnit(c.warn, c.unit)}`)
  if (c.crit != null) teile.push(`kritisch ${wort} ${formatWithUnit(c.crit, c.unit)}`)
  if (c.for_s > 0) teile.push(`nach ${Math.round(c.for_s / 60)} min`)
  return teile.join(' · ')
}

interface Props {
  deviceId: string
  latest: Sample[]
  darfVerwalten: boolean
}

/**
 * Eigene SNMP-Abfragen eines Geräts: OIDs, die kein Template liest, mit
 * Namen, Einheit und Schwellen. Der Agent liest eine neue Abfrage sofort;
 * der erste Wert steht wenige Sekunden später hier.
 */
export function EigeneAbfragen({ deviceId, latest, darfVerwalten }: Props) {
  const queryClient = useQueryClient()
  const [bearbeiten, setBearbeiten] = useState<CustomMetric | 'neu' | null>(null)
  const daten = useQuery({
    queryKey: ['device-abfragen', deviceId],
    queryFn: ({ signal }) => apiGet<CustomMetrics>(`/devices/${deviceId}/abfragen`, signal),
  })
  const abfragen = daten.data?.abfragen ?? []

  // Namen und Einheiten auch im Verlauf zeigen, nicht den Schlüssel.
  useEffect(() => {
    registerMetricInfo(
      (daten.data?.abfragen ?? []).map((a) => ({ key: a.key, label: a.name, unit: a.unit || undefined })),
    )
  }, [daten.data])

  const entfernen = useMutation({
    mutationFn: (key: string) => apiSend<void>('DELETE', `/devices/${deviceId}/abfragen/${key}`),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['device-abfragen', deviceId] }),
  })

  if (!daten.data?.abfragen || (abfragen.length === 0 && !darfVerwalten)) return null
  const voll = abfragen.length >= daten.data.max

  return (
    <section className="card">
      <div className="card-head">
        <h2 className="card-title">Eigene Abfragen</h2>
        {darfVerwalten && bearbeiten === null ? (
          <button type="button" className="btn btn-sm" disabled={voll} onClick={() => setBearbeiten('neu')}>
            Abfrage hinzufügen
          </button>
        ) : null}
      </div>
      {abfragen.length === 0 && bearbeiten === null ? (
        <div className="card-body muted" style={{ fontSize: 13 }}>
          Noch keine eigene Abfrage. Hier lässt sich jede OID lesen, die das Template nicht kennt – ein Lüfter, ein
          Sensor, ein Zähler aus der Hersteller-MIB –, mit eigenen Schwellen für Alarme.
        </div>
      ) : null}
      {abfragen.length > 0 ? (
        <table className="data">
          <thead>
            <tr>
              <th>Name</th>
              <th>OID</th>
              <th>Wert</th>
              <th>Alarm</th>
              <th>Abfrage</th>
              {darfVerwalten ? <th aria-label="Aktionen" /> : null}
            </tr>
          </thead>
          <tbody>
            {abfragen.map((a) => (
              <tr key={a.key}>
                <td>{a.name}</td>
                <td className="mono" style={{ fontSize: 12 }}>
                  {a.oid}
                  {a.table ? <span className="muted"> (Tabelle)</span> : null}
                </td>
                <td>
                  <Wert abfrage={a} latest={latest} />
                </td>
                <td style={{ fontSize: 13 }}>{schwellenText(a)}</td>
                <td className="muted" style={{ fontSize: 13 }}>
                  alle {INTERVALLE.find(([s]) => s === a.interval_s)?.[1] ?? `${a.interval_s} s`}
                </td>
                {darfVerwalten ? (
                  <td style={{ whiteSpace: 'nowrap', textAlign: 'right' }}>
                    <button
                      type="button"
                      className="btn btn-sm"
                      aria-label={`${a.name} bearbeiten`}
                      onClick={() => setBearbeiten(a)}
                    >
                      Bearbeiten
                    </button>{' '}
                    <button
                      type="button"
                      className="btn btn-sm"
                      aria-label={`${a.name} entfernen`}
                      disabled={entfernen.isPending}
                      onClick={() => entfernen.mutate(a.key)}
                    >
                      Entfernen
                    </button>
                  </td>
                ) : null}
              </tr>
            ))}
          </tbody>
        </table>
      ) : null}
      {entfernen.isError ? (
        <div className="card-body" style={{ color: 'var(--crit)' }}>
          {(entfernen.error as Error).message}
        </div>
      ) : null}
      {voll && darfVerwalten ? (
        <div className="card-body muted" style={{ fontSize: 12 }}>
          Höchstens {daten.data.max} eigene Abfragen je Gerät.
        </div>
      ) : null}
      {bearbeiten !== null ? (
        <AbfrageForm
          deviceId={deviceId}
          vorlage={bearbeiten === 'neu' ? null : bearbeiten}
          einheiten={daten.data.einheiten}
          onFertig={() => {
            setBearbeiten(null)
            void queryClient.invalidateQueries({ queryKey: ['device-abfragen', deviceId] })
          }}
          onAbbrechen={() => setBearbeiten(null)}
        />
      ) : null}
    </section>
  )
}

function Wert({ abfrage, latest }: { abfrage: CustomMetric; latest: Sample[] }) {
  const werte = latest.filter((s) => s.metric === abfrage.key)
  if (werte.length === 0) {
    return (
      <span
        className="muted"
        title="Kommt mit der nächsten Abfrage. Bleibt es leer, kennt das Gerät die OID nicht oder liefert Text statt einer Zahl."
      >
        noch kein Wert
      </span>
    )
  }
  if (!abfrage.table) return <span className="mono">{formatWithUnit(werte[0]!.value, abfrage.unit)}</span>
  const zeilen = [...werte].sort((a, b) =>
    (a.labels?.index ?? '').localeCompare(b.labels?.index ?? '', 'de', { numeric: true }),
  )
  return (
    <ul style={{ listStyle: 'none', margin: 0, padding: 0, fontSize: 13 }}>
      {zeilen.slice(0, 8).map((z) => (
        <li key={z.labels?.index}>
          <span className="muted">{z.labels?.zeile ?? z.labels?.index}:</span>{' '}
          <span className="mono">{formatWithUnit(z.value, abfrage.unit)}</span>
        </li>
      ))}
      {zeilen.length > 8 ? <li className="muted">und {zeilen.length - 8} weitere Zeilen</li> : null}
    </ul>
  )
}

interface FormProps {
  deviceId: string
  vorlage: CustomMetric | null
  einheiten: string[]
  onFertig: () => void
  onAbbrechen: () => void
}

function zahlOderNichts(text: string): number | null {
  const t = text.trim().replace(',', '.')
  if (!t) return null
  const n = Number(t)
  return Number.isFinite(n) ? n : NaN
}

function AbfrageForm({ deviceId, vorlage, einheiten, onFertig, onAbbrechen }: FormProps) {
  const [name, setName] = useState(vorlage?.name ?? '')
  const [oid, setOid] = useState(vorlage?.oid ?? '')
  const [tabelle, setTabelle] = useState(vorlage?.table ?? false)
  const [beschriftung, setBeschriftung] = useState(vorlage?.label_oid ?? '')
  const [einheit, setEinheit] = useState(vorlage?.unit ?? '')
  const [intervall, setIntervall] = useState(vorlage?.interval_s ?? 60)
  const [richtung, setRichtung] = useState<'above' | 'below'>(vorlage?.direction ?? 'above')
  const [warn, setWarn] = useState(vorlage?.warn != null ? String(vorlage.warn) : '')
  const [crit, setCrit] = useState(vorlage?.crit != null ? String(vorlage.crit) : '')
  const [dauer, setDauer] = useState(String(Math.round((vorlage?.for_s ?? 300) / 60)))

  const speichern = useMutation({
    mutationFn: () => {
      const body = {
        name: name.trim(),
        oid: oid.trim(),
        table: tabelle,
        label_oid: tabelle ? beschriftung.trim() : '',
        unit: einheit,
        interval_s: intervall,
        direction: richtung,
        warn: zahlOderNichts(warn),
        crit: zahlOderNichts(crit),
        for_s: Math.round(Number(dauer.replace(',', '.') || '0') * 60),
      }
      return vorlage
        ? apiSend<CustomMetric>('PUT', `/devices/${deviceId}/abfragen/${vorlage.key}`, body)
        : apiSend<CustomMetric>('POST', `/devices/${deviceId}/abfragen`, body)
    },
    onSuccess: onFertig,
  })

  const ungueltigeZahl = [warn, crit].some((t) => Number.isNaN(zahlOderNichts(t)))

  return (
    <form
      className="card-body stack"
      style={{ gap: 12, borderTop: '1px solid var(--line)' }}
      onSubmit={(e) => {
        e.preventDefault()
        if (!ungueltigeZahl) speichern.mutate()
      }}
    >
      <h3 className="card-title" style={{ fontSize: 14 }}>
        {vorlage ? `${vorlage.name} bearbeiten` : 'Abfrage hinzufügen'}
      </h3>
      <div className="row" style={{ gap: 12, flexWrap: 'wrap', alignItems: 'flex-end' }}>
        <div className="field" style={{ minWidth: 200, flex: 1 }}>
          <label htmlFor="abfrage-name">Name</label>
          <input
            id="abfrage-name"
            className="input"
            value={name}
            maxLength={64}
            placeholder="Lüfter 1"
            onChange={(e) => setName(e.target.value)}
          />
        </div>
        <div className="field" style={{ minWidth: 280, flex: 2 }}>
          <label htmlFor="abfrage-oid">OID</label>
          <input
            id="abfrage-oid"
            className="input mono"
            value={oid}
            placeholder="1.3.6.1.4.1.9.9.13.1.4.1.3.1"
            onChange={(e) => setOid(e.target.value)}
          />
        </div>
        <div className="field">
          <label htmlFor="abfrage-art">Art</label>
          <select
            id="abfrage-art"
            className="input"
            value={tabelle ? 'tabelle' : 'wert'}
            onChange={(e) => setTabelle(e.target.value === 'tabelle')}
          >
            <option value="wert">Einzelwert</option>
            <option value="tabelle">Tabelle (ein Wert je Zeile)</option>
          </select>
        </div>
      </div>
      {tabelle ? (
        <div className="field" style={{ maxWidth: 420 }}>
          <label htmlFor="abfrage-beschriftung">Beschriftung der Zeilen aus OID (optional)</label>
          <input
            id="abfrage-beschriftung"
            className="input mono"
            value={beschriftung}
            placeholder="1.3.6.1.4.1.9.9.13.1.4.1.2"
            onChange={(e) => setBeschriftung(e.target.value)}
          />
        </div>
      ) : null}
      <div className="row" style={{ gap: 12, flexWrap: 'wrap', alignItems: 'flex-end' }}>
        <div className="field">
          <label htmlFor="abfrage-einheit">Einheit</label>
          <select id="abfrage-einheit" className="input" value={einheit} onChange={(e) => setEinheit(e.target.value)}>
            {einheiten.map((u) => (
              <option key={u} value={u}>
                {EINHEIT_TEXT[u] ?? u}
              </option>
            ))}
          </select>
        </div>
        <div className="field">
          <label htmlFor="abfrage-intervall">Abfragen alle</label>
          <select
            id="abfrage-intervall"
            className="input"
            value={intervall}
            onChange={(e) => setIntervall(Number(e.target.value))}
          >
            {INTERVALLE.map(([s, text]) => (
              <option key={s} value={s}>
                {text}
              </option>
            ))}
          </select>
        </div>
        <div className="field">
          <label htmlFor="abfrage-richtung">Alarm, wenn der Wert</label>
          <select
            id="abfrage-richtung"
            className="input"
            value={richtung}
            onChange={(e) => setRichtung(e.target.value as 'above' | 'below')}
          >
            <option value="above">steigt (über der Schwelle)</option>
            <option value="below">fällt (unter der Schwelle)</option>
          </select>
        </div>
        <div className="field" style={{ width: 120 }}>
          <label htmlFor="abfrage-warn">Warnung</label>
          <input
            id="abfrage-warn"
            className="input mono"
            inputMode="decimal"
            value={warn}
            onChange={(e) => setWarn(e.target.value)}
          />
        </div>
        <div className="field" style={{ width: 120 }}>
          <label htmlFor="abfrage-crit">Kritisch</label>
          <input
            id="abfrage-crit"
            className="input mono"
            inputMode="decimal"
            value={crit}
            onChange={(e) => setCrit(e.target.value)}
          />
        </div>
        <div className="field" style={{ width: 130 }}>
          <label htmlFor="abfrage-dauer">Für mindestens (min)</label>
          <input
            id="abfrage-dauer"
            className="input mono"
            inputMode="numeric"
            value={dauer}
            onChange={(e) => setDauer(e.target.value)}
          />
        </div>
      </div>
      <span className="muted" style={{ fontSize: 12 }}>
        Nur Zahlen werden gespeichert. Ohne Warnung und ohne kritischen Wert gibt es keinen Alarm, nur den Verlauf. Eine
        OID ohne „.0“ am Ende wird auch mit „.0“ versucht.
      </span>
      {ungueltigeZahl ? <span style={{ color: 'var(--crit)' }}>Eine Schwelle ist keine Zahl.</span> : null}
      {speichern.isError ? <span style={{ color: 'var(--crit)' }}>{(speichern.error as Error).message}</span> : null}
      <div className="row" style={{ gap: 8 }}>
        <button
          type="submit"
          className="btn btn-primary"
          disabled={speichern.isPending || !name.trim() || !oid.trim() || ungueltigeZahl}
        >
          Speichern
        </button>
        <button type="button" className="btn" onClick={onAbbrechen}>
          Abbrechen
        </button>
      </div>
    </form>
  )
}
